/**
 * 이상한 방 - 로비 (여럿이 방의 대기실, 순수 DOM)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 3절(라운드 구조 - 다 모이면 시작) · 7.3절(링크 하나로 들어온다)
 *            `이상한방-상태모델-v0.1.md` 9.2절(인원이 모자라면 봇이 채운다) · 11.3절(서버가 쥐는 것 - 시작 가부는 리듀서)
 *            로드맵 M6(`lobby.ts` - 방 만들기 / 번호로 들어가기 / 참가자(사람·이웃) / 이웃 넣기·빼기 / 준비 / 시작(방장) / 방장 설정. 방 번호는 크게)
 *
 * ## 두 얼굴 - 폼과 방
 *
 * 아직 방에 없으면 **폼**이다: 이름 · [방 만들기] · 방 번호 · [번호로 들어가기]. 방에 앉으면 **방**이다: 번호 크게 · 참가자 · 이웃 넣기·빼기 ·
 * 방장 설정 · [준비] · [시작]. 두 얼굴을 한 화면에 둔 이유는 오류의 자리다 - "그 번호의 방이 없다"는 폼 위에 떠야 다시 적을 수 있고,
 * 방에서 끊기면(`superseded`) 폼으로 돌아와야 한다. 화면 하나가 두 상태를 갖는 것이 화면 둘이 상태를 나누는 것보다 단순했다.
 *
 * ## 계산하지 않는다
 *
 * [시작]이 눌리는가는 뷰의 불리언(`canStart` - 리듀서 `matchState.canStart`의 값)이고, 이웃을 더 넣을 수 있는가도 받은 값이다
 * (`app/lobbyFlow.toLobbyRoomView`가 `MAX_ROOM_SEATS`와 좌석 수로 낸다 - 서버 `room.ts`가 `full`을 내는 기준과 같은 상수).
 * 여기서 `seats.every(ready)`를 세면 그것이 리듀서 게이트의 사본이다. 이 파일이 아는 것은 받은 모양(`LobbyRoomView`)과 손이 무엇을
 * 눌렀는가뿐이다. 누르면 콜백 하나 - 가부는 호스트가 정하고, 다음 뷰가 화면을 바꾼다.
 *
 * ## 방 번호는 크게
 *
 * 이 화면의 존재 이유가 "옆 사람 폰에 네 글자를 불러 주는 것"이다(완료 판정 - 폰 2대 + 노트북이 `?room=ABCD`로 들어온다). 그래서
 * 번호가 화면에서 가장 큰 글자이고 노랑이다 - 이 화면에서 노랑이 붙는 자리는 번호 · 준비됨 태그 · 주 버튼 셋이다.
 *
 * ## 이름은 한 번 적는다
 *
 * 폼의 이름 칸은 마지막에 적은 이름으로 채워져 온다(`app/netBoot.rememberedName`) - 새로고침·다음 방에서 다시 적게 하면 그것이 곧 마찰이다.
 * 빈 이름은 기본 이름(`lobby.name.default`)으로 간다 - 서버는 빈 이름을 대신 지어 주지 않는다(사전이 없다, `gateway.tidyName`).
 * 길이는 `MAX_PLAYER_NAME_LENGTH`로 입력 칸이 막는다(서버가 자르는 값과 같은 상수).
 *
 * 문구는 전부 사전이다. 언어가 바뀌면 굳은 라벨을 다시 칠하고 마지막 상태를 다시 그린다(`onLocaleChange` - 봉투·사물함과 같은 결정).
 * 화면 중앙은 비우지 않는다 - 이 화면 뒤에는 볼 방이 없다(잠든 무대). 누르는 것은 전부 44px 이상(`--gh-touch`).
 */

import { onLocaleChange, t } from '../i18n'
import {
  MAX_PLAYER_NAME_LENGTH,
  normalizeRoomCode,
  tidyPlayerName,
  type ErrorCode,
  type RoomVisibility,
} from '../net/protocol'

import { createElement, setAttr, setDisabled, setFlag, setHidden, setText } from './dom'
import { createHostSettings, createSeatList, type LobbySeatRow, type LobbySettingsPatch, type LobbySettingsView } from './lobbySeats'

import './lobby.css'

export type { LobbySeatRow, LobbySettingsPatch, LobbySettingsView } from './lobbySeats'

/* --- 타입 ---------------------------------------------------------------------- */

/**
 * 폼 위에 뜰 수 있는 이유. 서버가 말한 것(`ErrorCode`)에 **`offline` 하나가 더 있다** - 소켓이 한 번도 열리지 못한 경우다.
 * 서버는 그 말을 할 수 없고(말할 소켓이 없다) 그렇다고 [방 만들기]가 영원히 "들어가는 중"으로 남으면 그건 고장으로 읽힌다.
 * 사전 키는 같은 묶음(`net.error.*`)이라 배너와 문법이 하나다.
 */
export type LobbyFormError = ErrorCode | 'offline'

/** 폼 얼굴의 상태. `busy`는 `hello`를 보내고 `welcome`을 기다리는 동안 - 버튼을 잠근다(두 번 누르면 방이 둘 생긴다). */
export type LobbyFormState = Readonly<{
  name: string
  /** 미리 채울 방 번호(`?room=CODE`로 들어왔을 때). 없으면 빈 칸. */
  code: string
  error: LobbyFormError | null
  busy: boolean
}>

/** 방 얼굴. 값은 전부 호스트의 뷰에서 옮겨 온 것이다(`app/lobbyFlow.toLobbyRoomView` - 산술은 좌석 수 비교 하나). */
export type LobbyRoomView = Readonly<{
  code: string
  seats: readonly LobbySeatRow[]
  seatsMax: number
  isHost: boolean
  meReady: boolean
  /** `SelfView.canStart` 그대로 - 방장 · 로비 · 전원 준비. */
  canStart: boolean
  canAddNeighbor: boolean
  canRemoveNeighbor: boolean
  settings: LobbySettingsView
  /**
   * 이 방이 목록에 뜨는가(M14). **뷰의 값이지 이 화면의 기억이 아니다** - 누른 순간 칠하면(낙관적) 서버가
   * 거절했을 때 화면만 바뀐 채 남는다. 토글은 손잡이를 부르고, 서버의 `visibility` 메시지가 다음 뷰를 만든다.
   */
  visibility: RoomVisibility
}>

export type LobbyHandlers = Readonly<{
  onCreate: (name: string) => void
  onJoin: (code: string, name: string) => void
  /** 폼에서 뒤로 - 단계 선택으로. */
  onBack: () => void
  onAddNeighbor: () => void
  onRemoveNeighbor: () => void
  onReady: (ready: boolean) => void
  onStart: () => void
  onConfigure: (patch: LobbySettingsPatch) => void
  /** 공개↔비공개(M14 규칙 5). 방장만 누를 수 있고, 방장이 아니면 버튼 자체가 서지 않는다. */
  onVisibility: (value: RoomVisibility) => void
  /** 방에서 나가기. 좌석을 떠난다 - 로비에서는 자리가 사라진다(리듀서 `onLeave`). */
  onLeave: () => void
}>

export type LobbyHandle = Readonly<{
  root: HTMLElement
  showForm: (state: LobbyFormState) => void
  showRoom: (view: LobbyRoomView) => void
  /** 폼 위의 오류 줄. `null`이면 지운다. 방 얼굴에서는 폼으로 돌아간 뒤 띄워야 보인다. */
  setError: (error: LobbyFormError | null) => void
  setBusy: (busy: boolean) => void
  hide: () => void
  isVisible: () => boolean
  dispose: () => void
}>

export type LobbyOptions = Readonly<{ mount: HTMLElement; handlers: LobbyHandlers }>

/* --- 조각 ---------------------------------------------------------------------- */

/**
 * 입력 칸의 방 번호. 다듬기의 원본은 와이어 파일이다(`protocol.normalizeRoomCode` - 서버의 발급·검사와 같은 함수) -
 * 폰이 소문자로 받아 적어도 같은 방이고, `?room=`(`urlState.resolveRoomCode`)도 같은 것을 지난다.
 */
export function tidyCodeInput(raw: string): string {
  return normalizeRoomCode(raw)
}

/** 빈 이름은 기본 이름으로(머리말). 길이 자르기는 서버와 **같은 함수**다(`protocol.tidyPlayerName`) - 입력 칸의 `maxLength`는 붙여 넣기가 우회한다. */
export function tidyNameInput(raw: string): string {
  const trimmed = tidyPlayerName(raw)
  return trimmed === '' ? t('lobby.name.default') : trimmed
}

function button(className: string): HTMLButtonElement {
  const element = createElement('button', className, '')
  element.type = 'button'
  return element
}

/* --- 본체 ---------------------------------------------------------------------- */

export function createLobby(options: LobbyOptions): LobbyHandle {
  const { handlers } = options
  const root = createElement('section', 'lb-root')
  root.hidden = true

  /* --- 머리 --- */
  const head = createElement('header', 'lb-head')
  const eyebrow = createElement('p', 'lb-eyebrow', '')
  const title = createElement('h1', 'lb-title', '')
  head.append(eyebrow, title)

  /* --- 폼 --- */
  const form = createElement('div', 'lb-form')
  const nameField = createElement('label', 'lb-field')
  const nameLabel = createElement('span', 'lb-label', '')
  const nameInput = createElement('input', 'lb-input')
  nameInput.type = 'text'
  nameInput.maxLength = MAX_PLAYER_NAME_LENGTH
  // `autocomplete` 속성의 타입(`AutoFill`)에 'nickname'이 없다 - 브라우저는 받는 값이라 속성으로 직접 적는다.
  setAttr(nameInput, 'autocomplete', 'nickname')
  nameField.append(nameLabel, nameInput)
  const createButton = button('lb-primary')
  const codeField = createElement('label', 'lb-field')
  const codeLabel = createElement('span', 'lb-label', '')
  const codeInput = createElement('input', 'lb-input lb-input--code')
  codeInput.type = 'text'
  codeInput.autocomplete = 'off'
  codeInput.autocapitalize = 'characters'
  codeInput.spellcheck = false
  codeField.append(codeLabel, codeInput)
  const joinButton = button('lb-secondary')
  const errorLine = createElement('p', 'lb-error', '')
  errorLine.setAttribute('role', 'alert')
  errorLine.hidden = true
  const busyLine = createElement('p', 'lb-busy', '')
  busyLine.hidden = true
  form.append(nameField, createButton, codeField, joinButton, errorLine, busyLine)

  /* --- 방 --- */
  const room = createElement('div', 'lb-room')
  const codePlate = createElement('div', 'lb-code-plate')
  const code = createElement('p', 'lb-code', '')
  const share = createElement('p', 'lb-share', '')
  /*
   * 공개 여부(M14). 번호 플레이트 안에 있는 이유는 둘이 같은 것을 말하기 때문이다 - "이 방에 어떻게 들어오는가".
   * 번호는 불러 주는 길이고 이 토글은 목록에 뜨는 길이다. 방장이 아니면 만들지 않는다(감추는 것이 아니다).
   */
  const visibilityButton = button('lb-chip lb-visibility')
  codePlate.append(code, share, visibilityButton)
  const seats = createSeatList()
  const neighbors = createElement('div', 'lb-neighbors')
  const addNeighbor = button('lb-secondary lb-secondary--small')
  const removeNeighbor = button('lb-secondary lb-secondary--small')
  neighbors.append(addNeighbor, removeNeighbor)
  const settings = createHostSettings((patch) => handlers.onConfigure(patch))
  const hint = createElement('p', 'lb-hint', '')
  room.append(codePlate, seats.root, neighbors, settings.root, hint)

  /* --- 아래 버튼 줄 --- */
  const actions = createElement('div', 'lb-actions')
  const backButton = button('lb-back')
  const readyButton = button('lb-primary lb-primary--ready')
  const startButton = button('lb-primary lb-primary--start')
  const leaveButton = button('lb-back')
  actions.append(readyButton, startButton, backButton, leaveButton)

  root.append(head, form, room, actions)
  options.mount.append(root)

  /* --- 상태 --- */
  let face: 'form' | 'room' = 'form'
  let lastForm: LobbyFormState | null = null
  let lastRoom: LobbyRoomView | null = null
  /** 폼이 답을 기다리는 중인가. 버튼의 얼굴이 아니라 손의 잠금이다(아래 `join`·`create`). */
  let busy = false

  /* --- 손 --- */
  /*
   * `busy`(= `hello`를 보내고 `welcome`을 기다리는 중)는 두 버튼의 `disabled`로 보이지만, 잠금은 **여기서** 건다.
   * 번호 칸의 Enter는 버튼을 누르는 것이 아니라 `join()`을 직접 부르므로 버튼만 잠그면 그 길이 열려 있고, 느린 회선에서
   * 초조하게 Enter를 치는 동작이 곧 "앞 소켓을 끊고 새로 연다"(`lobbyFlow.connectAs`의 `leaveRoom`)가 된다.
   */
  const create = (): void => {
    if (busy) return
    handlers.onCreate(tidyNameInput(nameInput.value))
  }
  createButton.addEventListener('click', create)
  const join = (): void => {
    if (busy) return
    const value = tidyCodeInput(codeInput.value)
    if (value === '') {
      codeInput.focus()
      return
    }
    handlers.onJoin(value, tidyNameInput(nameInput.value))
  }
  joinButton.addEventListener('click', join)
  codeInput.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') join()
  })
  codeInput.addEventListener('input', () => {
    const tidy = tidyCodeInput(codeInput.value)
    if (codeInput.value !== tidy) codeInput.value = tidy
  })
  backButton.addEventListener('click', () => handlers.onBack())
  addNeighbor.addEventListener('click', () => handlers.onAddNeighbor())
  removeNeighbor.addEventListener('click', () => handlers.onRemoveNeighbor())
  readyButton.addEventListener('click', () => handlers.onReady(!(lastRoom?.meReady ?? false)))
  startButton.addEventListener('click', () => handlers.onStart())
  // 지금 값의 **반대**를 청한다. 스위치가 아니라 요청이라 화면은 여기서 바뀌지 않는다(`LobbyRoomView.visibility` 주석).
  visibilityButton.addEventListener('click', () => {
    handlers.onVisibility(lastRoom?.visibility === 'public' ? 'private' : 'public')
  })
  leaveButton.addEventListener('click', () => handlers.onLeave())

  /* --- 칠하기 --- */

  /** 언어에만 기대는 글자. 언어가 바뀌면 다시 칠한다. */
  function paintStatic(): void {
    setText(eyebrow, t('lobby.eyebrow'))
    setText(nameLabel, t('lobby.name.label'))
    setText(createButton, t('lobby.create'))
    setText(codeLabel, t('lobby.code.label'))
    setText(joinButton, t('lobby.join'))
    setText(backButton, t('lobby.back'))
    setText(busyLine, t('lobby.connecting'))
    setText(share, t('lobby.share'))
    setText(addNeighbor, t('lobby.addNeighbor'))
    setText(removeNeighbor, t('lobby.removeNeighbor'))
    setText(startButton, t('lobby.start'))
    setText(leaveButton, t('match.leave'))
    settings.repaint()
  }

  function paintFace(): void {
    const isForm = face === 'form'
    setText(title, isForm ? t('lobby.form.title') : t('phase.lobby.hint'))
    setHidden(form, !isForm)
    setHidden(backButton, !isForm)
    setHidden(room, isForm)
    setHidden(readyButton, isForm)
    setHidden(startButton, isForm)
    setHidden(leaveButton, isForm)
    setAttr(root, 'data-face', face)
  }

  function paintForm(state: LobbyFormState): void {
    // 입력 칸은 사람이 적는 중일 수 있다 - 값이 왔을 때만 덮는다(빈 상태로 덮으면 적던 이름이 지워진다).
    if (state.name !== '' && nameInput.value === '') nameInput.value = state.name
    if (state.code !== '' && codeInput.value === '') codeInput.value = tidyCodeInput(state.code)
    paintError(state.error)
    paintBusy(state.busy)
  }

  function paintError(error: LobbyFormError | null): void {
    setHidden(errorLine, error === null)
    if (error !== null) setText(errorLine, t(`net.error.${error}`))
  }

  function paintBusy(next: boolean): void {
    busy = next
    setHidden(busyLine, !next)
    setDisabled(createButton, next)
    setDisabled(joinButton, next)
    setFlag(form, 'is-busy', next)
  }

  function paintRoom(view: LobbyRoomView): void {
    setText(code, view.code)
    setAttr(code, 'aria-label', t('lobby.codeAria', { code: view.code }))
    seats.render(view.seats, view.seatsMax)
    setHidden(neighbors, !view.isHost)
    setDisabled(addNeighbor, !view.canAddNeighbor)
    setDisabled(removeNeighbor, !view.canRemoveNeighbor)
    settings.render(view.settings, view.isHost)
    const isPublic = view.visibility === 'public'
    setHidden(visibilityButton, !view.isHost)
    setText(visibilityButton, isPublic ? t('lobby.visibility.public') : t('lobby.visibility.private'))
    setAttr(visibilityButton, 'aria-pressed', isPublic ? 'true' : 'false')
    setAttr(visibilityButton, 'aria-label', t('lobby.visibility.aria', { state: t(isPublic ? 'lobby.visibility.public' : 'lobby.visibility.private') }))
    setText(readyButton, view.meReady ? t('lobby.unready') : t('lobby.ready'))
    setAttr(readyButton, 'aria-pressed', view.meReady ? 'true' : 'false')
    setHidden(startButton, !view.isHost)
    setDisabled(startButton, !view.canStart)
    setText(hint, view.isHost ? t('lobby.startHint') : t('lobby.startWait'))
  }

  paintStatic()
  paintFace()

  const stopLocaleWatch = onLocaleChange(() => {
    paintStatic()
    paintFace()
    if (face === 'form' && lastForm !== null) paintForm(lastForm)
    if (face === 'room' && lastRoom !== null) paintRoom(lastRoom)
  })

  return {
    root,

    showForm(state): void {
      face = 'form'
      lastForm = state
      lastRoom = null
      paintFace()
      paintForm(state)
      root.hidden = false
    },

    showRoom(view): void {
      face = 'room'
      lastRoom = view
      paintFace()
      paintRoom(view)
      root.hidden = false
    },

    setError(error): void {
      if (lastForm !== null) lastForm = { ...lastForm, error }
      paintError(error)
    },

    setBusy(next): void {
      if (lastForm !== null) lastForm = { ...lastForm, busy: next }
      paintBusy(next)
    },

    hide(): void {
      root.hidden = true
    },

    isVisible: () => !root.hidden,

    dispose(): void {
      stopLocaleWatch()
      root.remove()
    },
  }
}
